import {
    getPanelScrollBottomInset,
    getPanelScrollTopInset,
    PANEL_SCROLL_BOTTOM_GAP_PX,
} from 'lib/panelEndGutter'

// Posting is not navigation, so the reader is never moved. What they get
// instead depends on where the new reply actually landed: if enough of it is
// on screen it simply flashes in place, and only a reply below the fold is
// worth a pill offering the trip. A reply posted from the bottom editor that
// shows only in part, or at all from the thread's end, is the one exception:
// it is brought the rest of the way into view (planBottomEditorPostedAnswer).
export type PostedAnswerFeedback = 'highlight' | 'offer'

// A sliver peeking past the fold is not something a flash can announce — the
// reader would see a tint on a row they cannot read. Roughly a line of text.
export const POSTED_ANSWER_MIN_VISIBLE_PX = 32

export interface PostedAnswerVisibility {
    rowTop: number
    rowBottom: number
    viewportTop: number
    viewportBottom: number
}

export function planPostedAnswerFeedback(
    { rowTop, rowBottom, viewportTop, viewportBottom }: PostedAnswerVisibility,
    minVisiblePx = POSTED_ANSWER_MIN_VISIBLE_PX,
): PostedAnswerFeedback {
    const rowHeight = rowBottom - rowTop
    if (!Number.isFinite(rowHeight) || rowHeight <= 0) return 'offer'

    const visible = Math.min(rowBottom, viewportBottom) - Math.max(rowTop, viewportTop)
    // A row shorter than the threshold only has to be wholly visible.
    return visible >= Math.min(minVisiblePx, rowHeight) ? 'highlight' : 'offer'
}

// A reply posted from the bottom editor, which floats over the thread's foot:
// seen whole, it flashes in place; seen in part (at least the modal's line of
// it), it is revealed; not seen, it is offered with the modal's pill. Posted
// by a reader at the thread's end (followEnd), it is revealed however little
// of it shows below the view: writing several posts in a row there, the
// thread advances with each one, as a chat does. Following only ever moves
// the thread down.
export type BottomEditorPostedAnswerFeedback = 'highlight' | 'reveal' | 'offer'

export function planBottomEditorPostedAnswer(
    { rowTop, rowBottom, viewportTop, viewportBottom }: PostedAnswerVisibility,
    {
        followEnd = false,
        minVisiblePx = POSTED_ANSWER_MIN_VISIBLE_PX,
    }: { followEnd?: boolean; minVisiblePx?: number } = {},
): BottomEditorPostedAnswerFeedback {
    const rowHeight = rowBottom - rowTop
    if (!Number.isFinite(rowHeight) || rowHeight <= 0) return 'offer'
    if (rowTop >= viewportTop && rowBottom <= viewportBottom) return 'highlight'
    if (followEnd && rowTop >= viewportTop) return 'reveal'

    const visible = Math.min(rowBottom, viewportBottom) - Math.max(rowTop, viewportTop)
    return visible >= Math.min(minVisiblePx, rowHeight) ? 'reveal' : 'offer'
}

// Whether the reader is still reading the thread's end once it has left the
// view. The end leaving without the thread moving up — the keyboard shrinking
// the view, a reply appended below it, the advance to that reply — is not the
// reader leaving it; the thread moving up (more than the slack) is. An end
// never seen was never being read.
export function resolveReadingThreadEnd({
    endVisible,
    scrollTop,
    endSeenScrollTop,
    slackPx,
}: {
    endVisible: boolean
    scrollTop: number
    endSeenScrollTop: number | null
    slackPx: number
}): boolean {
    if (endVisible) return true
    if (endSeenScrollTop == null) return false
    return scrollTop >= endSeenScrollTop - slackPx
}

// Where a revealed reply is scrolled to: until its bottom clears the editor,
// or until its top stands where an answer link puts it (answerTopScrollTop,
// getAnswerTargetScrollTop), whichever comes first — a reply taller than the
// view stops with its top in place and the rest below.
export function resolvePostedAnswerRevealScrollTop({
    scrollTop,
    visibility: { rowBottom, viewportBottom },
    answerTopScrollTop,
}: {
    scrollTop: number
    visibility: PostedAnswerVisibility
    answerTopScrollTop: number
}): number {
    if (rowBottom > viewportBottom) {
        return Math.min(scrollTop + (rowBottom - viewportBottom), answerTopScrollTop)
    }
    // Cut off at the top instead: its top comes down to the answer link's place.
    return answerTopScrollTop
}

// The top edge of the bottom editor's pill in the panel's host, or null when
// the panel has none.
export function readBottomEditorTop(host: HTMLElement | null | undefined): number | null {
    const pill = host?.querySelector?.<HTMLElement>('[data-bottom-editor-pill="true"]') || host
    if (!pill) return null
    const top = pill.getBoundingClientRect().top
    return Number.isFinite(top) ? top : null
}

// The panel's own obstructions decide what "on screen" means: the floating
// thread capsule covers the top of the scroller and the New-thread button the
// bottom, so a row behind either is not somewhere a flash can be seen. A
// bottom editor floating over the scroller's foot (editorTop) is one more: the
// thread is seen only above it.
export function measurePostedAnswerVisibility(
    scrollContainer: HTMLElement,
    rowElement: HTMLElement,
    editorTop?: number | null,
): PostedAnswerVisibility {
    const containerRect = scrollContainer.getBoundingClientRect()
    const rowRect = rowElement.getBoundingClientRect()
    const viewportBottom = containerRect.bottom - getPanelScrollBottomInset(scrollContainer)

    return {
        rowTop: rowRect.top,
        rowBottom: rowRect.bottom,
        viewportTop: containerRect.top + getPanelScrollTopInset(scrollContainer),
        viewportBottom:
            editorTop != null && Number.isFinite(editorTop)
                ? Math.min(viewportBottom, editorTop - PANEL_SCROLL_BOTTOM_GAP_PX)
                : viewportBottom,
    }
}
